// src/services/azureCostEstimateVariantsService.js
// Azure Table Storage Service für Kostenschätzungs-Varianten

import { ensureTableExists } from "./azureTableUtils.js";

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "costEstimateVariants",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const baseUrl = () =>
  `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}`;

function safeJsonParse(str, fallback) {
  if (!str) return fallback;
  try { return JSON.parse(str); } catch { return fallback; }
}

function toBool(val) {
  return val === true || val === "true";
}

// Alle Varianten eines Dossiers für eine Phase laden
export const getVariantsByEstimate = async (estimateId, phase) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    let allEntities = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    const filter = phase
      ? `$filter=PartitionKey eq '${estimateId}' and Phase eq '${phase}'`
      : `$filter=PartitionKey eq '${estimateId}'`;

    do {
      let url = `${baseUrl()}?${AZURE_CONFIG.sasToken}&${filter}`;

      if (nextPartitionKey || nextRowKey) {
        if (nextPartitionKey) url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        if (nextRowKey) url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        if (response.status === 404) break;
        throw new Error(`Azure query error: ${response.status}`);
      }

      const data = await response.json();
      allEntities = allEntities.concat(data.value || []);

      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey || nextRowKey);

    return allEntities.map((entity) => ({
      id: entity.RowKey,
      name: entity.Name || "",
      phase: entity.Phase || "",
      isActive: toBool(entity.IsActive),
      positions: safeJsonParse(entity.Positions, []),
      createdAt: entity.CreatedAt || "",
      updatedAt: entity.UpdatedAt || "",
    }));
  } catch (error) {
    console.error("Load variants error:", error);
    return [];
  }
};

// Variante speichern (neu)
export const saveVariant = async (estimateId, variantData) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const variantId = crypto.randomUUID();
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: estimateId,
      RowKey: variantId,
      Name: variantData.name || "",
      Phase: variantData.phase || "",
      IsActive: variantData.isActive || false,
      Positions: JSON.stringify(variantData.positions || []),
      CreatedAt: now,
      UpdatedAt: now,
    };

    // MERGE-then-INSERT upsert
    const pk = encodeURIComponent(estimateId);
    const rk = encodeURIComponent(variantId);
    const url = `${baseUrl()}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;

    let response = await fetch(url, {
      method: "MERGE",
      headers: { "Content-Type": "application/json", "If-Match": "*" },
      body: JSON.stringify(entity),
    });

    if (response.status === 404) {
      const insertUrl = `${baseUrl()}?${AZURE_CONFIG.sasToken}`;
      response = await fetch(insertUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json;odata=nometadata",
        },
        body: JSON.stringify(entity),
      });
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure save variant error: ${response.status} - ${errorText}`);
    }

    return {
      id: variantId,
      name: variantData.name || "",
      phase: variantData.phase || "",
      isActive: variantData.isActive || false,
      positions: variantData.positions || [],
      createdAt: now,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Save variant error:", error);
    throw error;
  }
};

// Variante aktualisieren
export const updateVariant = async (estimateId, variantId, variantData) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const now = new Date().toISOString();

    const entity = {
      PartitionKey: estimateId,
      RowKey: variantId,
      Name: variantData.name || "",
      Phase: variantData.phase || "",
      IsActive: variantData.isActive || false,
      Positions: JSON.stringify(variantData.positions || []),
      CreatedAt: variantData.createdAt || "",
      UpdatedAt: now,
    };

    const pk = encodeURIComponent(estimateId);
    const rk = encodeURIComponent(variantId);
    const url = `${baseUrl()}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;

    let response = await fetch(url, {
      method: "MERGE",
      headers: { "Content-Type": "application/json", "If-Match": "*" },
      body: JSON.stringify(entity),
    });

    if (response.status === 404) {
      const insertUrl = `${baseUrl()}?${AZURE_CONFIG.sasToken}`;
      response = await fetch(insertUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json;odata=nometadata",
        },
        body: JSON.stringify(entity),
      });
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure update variant error: ${response.status} - ${errorText}`);
    }

    return {
      id: variantId,
      ...variantData,
      updatedAt: now,
    };
  } catch (error) {
    console.error("Update variant error:", error);
    throw error;
  }
};

// Alle Varianten eines Quell-Dossiers (alle Phasen) ins Ziel-Dossier kopieren.
// Neue variantIds werden vergeben; bkpCodes in den Positions bleiben gleich.
export const copyVariantsToEstimate = async (sourceEstimateId, targetEstimateId) => {
  const variants = await getVariantsByEstimate(sourceEstimateId);
  for (const v of variants) {
    await saveVariant(targetEstimateId, {
      name: v.name,
      phase: v.phase,
      isActive: v.isActive,
      positions: v.positions,
    });
  }
  return variants.length;
};

// Phase umbenennen (US-KOST-019): alle Varianten der alten Phase auf den neuen
// Phasennamen umschreiben (Phase ist reine Property, RowKey = variantId).
export const renameVariantsPhase = async (estimateId, oldPhase, newPhase) => {
  const variants = await getVariantsByEstimate(estimateId, oldPhase);
  for (const v of variants) {
    await updateVariant(estimateId, v.id, { ...v, phase: newPhase });
  }
  return variants.length;
};

// Phase löschen (US-KOST-019): alle Varianten der Phase endgültig entfernen.
export const deleteVariantsPhase = async (estimateId, phase) => {
  const variants = await getVariantsByEstimate(estimateId, phase);
  for (const v of variants) {
    await deleteVariant(estimateId, v.id);
  }
  return variants.length;
};

// Variante löschen
export const deleteVariant = async (estimateId, variantId) => {
  try {
    await ensureTableExists(AZURE_CONFIG.tableName);
    const pk = encodeURIComponent(estimateId);
    const rk = encodeURIComponent(variantId);
    const url = `${baseUrl()}(PartitionKey='${pk}',RowKey='${rk}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Azure delete variant error: ${response.status}`);
    }
  } catch (error) {
    console.error("Delete variant error:", error);
    throw error;
  }
};
